import { useEffect, useState, useCallback } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';
import { checkMySign, deleteTemporaryDoc, removeDocument } from '@entities/document/api/document';
import {
  captureHtmlContentAsBase64,
  DOCUMENT_CAPTURE_DEFAULTS,
} from '@shared/utils/captureDocumentImage';

import buildDocumentData from '../utils/buildDocumentData';

import { useSaveDocumentMutation, useUploadDocumentFileMutation } from './index';
import useAutoSaveDraft from './useAutoSaveDraft';

/**
 * 문서 저장(상신/임시저장) 로직 관리 커스텀 훅
 * - PC 버전과 동일한 로직 적용
 * - 자동 임시저장 기능 포함 (30초 간격)
 * - 네트워크 오류 시 자동 재시도
 * @param {Object} params - 문서 저장 파라미터
 * @param {Object} params.formInfo - 양식 정보
 * @param {Object} params.documentInfo - 문서 정보
 * @param {string} params.editorContent - 에디터 내용
 * @param {Array} params.referenceDocuments - 참조문서 목록
 * @param {Array} params.attachments - 첨부파일 목록
 * @param {Function} params.showAlert - Alert 표시 함수
 * @param {Function} [params.onSignRequired] - 서명 등록 필요 시 호출되는 콜백
 * @param {Function} [params.onSubmitSuccess] - 상신 성공 시 호출되는 콜백
 * @param {Function} [params.onTempSaveSuccess] - 임시저장 성공 시 호출되는 콜백
 * @param {Function} [params.onDraftRestore] - 임시저장 복구 시 호출되는 콜백
 * @param {React.RefObject} [params.editorRef] - 에디터 영역 ref (이미지 캡쳐용)
 * @returns {Object} 저장 핸들러 함수들
 */
function useDocumentSave({
  formInfo,
  documentInfo,
  editorContent,
  referenceDocuments,
  attachments = [],
  summaryNote = '',
  useCorporateSeal = false,
  showAlert,
  showConfirm,
  onSignRequired,
  onSubmitSuccess,
  onTempSaveSuccess,
  onDraftRestore,
  isEditMode = false,
  isRewriteMode = false,
  docIdx = null,
  docState = null,
  enableAutoSave = true,
  editorRef = null,
  // [WEB 기획 제외] PC 버전에서는 보안등급/긴급여부 기능이 있으나, WEB 버전에서는 기획에서 제외됨
  // securityInfo = { security: 3, hurry: 0 },
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 현재 진행 중인 작업 타입 ('temp' | 'submit' | null)
  const [submitType, setSubmitType] = useState(null);

  const companyCode = useAuthStore(function (state) {
    const code = state.companyCode;
    return code ? Number(code) : null;
  });
  const enumber = useAuthStore(function (state) {
    const code = state.enumber;
    return code ? Number(code) : null;
  });

  // 자동 임시저장 훅 (수정 모드에서는 비활성화, 단 재작성 모드는 활성화)
  const { saveDraft, loadDraft, clearDraft, hasDraft, getDraftSavedTime } = useAutoSaveDraft({
    formInfo,
    documentInfo,
    editorContent,
    referenceDocuments,
    attachments,
    isEnabled: enableAutoSave && (!isEditMode || isRewriteMode),
  });

  // 파일 업로드 Mutation
  const uploadDocumentFileMutation = useUploadDocumentFileMutation();

  // 문서 저장 Mutation
  const saveDocumentMutation = useSaveDocumentMutation({
    async onSuccess(data, variables) {
      if (data?.result_code === 0) {
        const savedDocIdx = data?.resultData;
        const isTempSave = variables?.mode === 3; // mode 3: 임시저장

        // 임시저장인 경우
        if (isTempSave) {
          // 실제 파일 객체가 있는 신규 첨부파일만 필터링
          const newAttachments = attachments.filter(function (fileObj) {
            return fileObj.file;
          });

          // 신규 첨부파일이 있을 경우에만 업로드 수행
          if (newAttachments.length > 0) {
            try {
              // 파일 업로드 재시도 로직 (최대 2회 재시도)
              const uploadWithRetry = async function (fileObj, maxRetries = 2) {
                let lastError;
                for (let attempt = 0; attempt <= maxRetries; attempt++) {
                  try {
                    return await uploadDocumentFileMutation.mutateAsync({
                      docIdx: savedDocIdx,
                      file: fileObj.file,
                    });
                  } catch (error) {
                    lastError = error;
                    // 타임아웃 오류이고 재시도 가능한 경우에만 재시도
                    if (
                      attempt < maxRetries &&
                      (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout'))
                    ) {
                      console.warn(
                        `파일 업로드 재시도 (${attempt + 1}/${maxRetries}): ${fileObj.name}`,
                      );
                      // 재시도 전 대기 (지수 백오프: 1초, 2초)
                      await new Promise(function (resolve) {
                        return setTimeout(resolve, (attempt + 1) * 1000);
                      });
                      continue;
                    }
                    throw error;
                  }
                }
                throw lastError;
              };

              const uploadPromises = newAttachments.map(function (fileObj) {
                return uploadWithRetry(fileObj);
              });

              await Promise.all(uploadPromises);
              // 모든 작업 완료 후 로딩 해제
              setIsSubmitting(false);
              setSubmitType(null);
              showAlert('임시저장되었습니다.', onTempSaveSuccess);
            } catch (error) {
              console.error('파일 업로드 중 오류 발생:', error);
              setIsSubmitting(false);
              setSubmitType(null);

              // 파일 업로드 실패 시 문서도 삭제 (롤백)
              try {
                await deleteTemporaryDoc(savedDocIdx);
                console.log('파일 업로드 실패로 인해 임시저장 문서가 삭제되었습니다.');
              } catch (deleteError) {
                console.error('문서 삭제 중 오류 발생:', deleteError);
              }

              // 타임아웃 오류인 경우 더 자세한 메시지 표시
              if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                showAlert(
                  '파일 업로드 시간이 초과되었습니다.\n첨부파일 업로드에 실패하여 문서 저장이 취소되었습니다.',
                );
              } else {
                showAlert('첨부파일 업로드에 실패하여 문서 저장이 취소되었습니다.');
              }
              // 문서가 삭제되었으므로 성공 콜백 호출하지 않음
            }
          } else {
            // 첨부파일 없는 경우 로딩 해제
            setIsSubmitting(false);
            setSubmitType(null);
            showAlert('임시저장되었습니다.', onTempSaveSuccess);
          }
          return;
        }

        // 상신 성공 시 로컬 임시저장 데이터 삭제
        clearDraft();

        // 임시저장 문서를 불러와서 상신한 경우: 임시저장 문서 삭제
        const isTempDocSubmission = isEditMode && docState === 0 && docIdx;
        if (isTempDocSubmission) {
          try {
            await deleteTemporaryDoc(docIdx);
          } catch (deleteError) {
            // 임시저장 문서 삭제 실패해도 상신은 성공했으므로 로그만 남기고 계속 진행
            console.warn('임시저장 문서 삭제 실패:', deleteError);
          }
        }

        // 실제 파일 객체가 있는 신규 첨부파일만 필터링
        const newAttachments = attachments.filter(function (fileObj) {
          return fileObj.file;
        });

        // 신규 첨부파일이 있을 경우에만 업로드 수행
        if (newAttachments.length > 0) {
          try {
            // 파일 업로드 재시도 로직 (최대 2회 재시도)
            const uploadWithRetry = async function (fileObj, maxRetries = 2) {
              let lastError;
              for (let attempt = 0; attempt <= maxRetries; attempt++) {
                try {
                  return await uploadDocumentFileMutation.mutateAsync({
                    docIdx: savedDocIdx,
                    file: fileObj.file,
                  });
                } catch (error) {
                  lastError = error;
                  // 타임아웃 오류이고 재시도 가능한 경우에만 재시도
                  if (
                    attempt < maxRetries &&
                    (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout'))
                  ) {
                    console.warn(
                      `파일 업로드 재시도 (${attempt + 1}/${maxRetries}): ${fileObj.name}`,
                    );
                    // 재시도 전 대기 (지수 백오프: 1초, 2초)
                    await new Promise(function (resolve) {
                      return setTimeout(resolve, (attempt + 1) * 1000);
                    });
                    continue;
                  }
                  throw error;
                }
              }
              throw lastError;
            };

            const uploadPromises = newAttachments.map(function (fileObj) {
              return uploadWithRetry(fileObj);
            });

            await Promise.all(uploadPromises);
            // 모든 작업 완료 후 로딩 해제
            setIsSubmitting(false);
            setSubmitType(null);
            showAlert('상신이 완료되었습니다.', onSubmitSuccess);
          } catch (error) {
            console.error('파일 업로드 중 오류 발생:', error);
            setIsSubmitting(false);
            setSubmitType(null);

            // 파일 업로드 실패 시 문서도 삭제 (롤백)
            try {
              await removeDocument(savedDocIdx);
              console.log('파일 업로드 실패로 인해 상신 문서가 삭제되었습니다.');
            } catch (deleteError) {
              console.error('문서 삭제 중 오류 발생:', deleteError);
            }

            // 타임아웃 오류인 경우 더 자세한 메시지 표시
            if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
              showAlert(
                '파일 업로드 시간이 초과되었습니다.\n첨부파일 업로드에 실패하여 상신이 취소되었습니다.',
              );
            } else {
              showAlert('첨부파일 업로드에 실패하여 상신이 취소되었습니다.');
            }
            // 문서가 삭제되었으므로 성공 콜백 호출하지 않음
          }
        } else {
          // 첨부파일 없는 경우 로딩 해제
          setIsSubmitting(false);
          setSubmitType(null);
          showAlert('상신이 완료되었습니다.', onSubmitSuccess);
        }
      } else {
        setIsSubmitting(false);
        setSubmitType(null);
        showAlert(data?.error_msg || '문서 저장에 실패했습니다.');
      }
    },
    onError(error) {
      setIsSubmitting(false);
      setSubmitType(null);
      console.error('문서 저장 오류:', error);

      // 네트워크 오류와 서버 오류 구분
      if (error?.message?.includes('Network Error') || !error?.response) {
        showAlert('네트워크 연결을 확인해주세요.\n자동 재시도 후에도 실패했습니다.');
      } else {
        showAlert('문서 저장 중 오류가 발생했습니다.');
      }
    },
  });

  // 임시저장 핸들러
  function handleTempSave() {
    // formId가 null, undefined, 또는 빈 문자열인 경우에만 에러 처리
    // formId가 0인 경우도 유효한 값으로 처리
    if (formInfo.formId == null || formInfo.formId === '') {
      showAlert('양식을 선택해주세요.');
      return;
    }

    if (!companyCode || !enumber) {
      showAlert('로그인 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitType('temp'); // 임시저장 타입 설정

    const documentData = buildDocumentData({
      mode: 3, // 임시저장
      companyCode,
      enumber,
      docType: formInfo.formId,
      title: documentInfo.subject,
      contents: editorContent,
      // [WEB 기획 제외] PC 버전에서는 보안등급/긴급여부 기능이 있으나, WEB 버전에서는 기획에서 제외됨
      // security: securityInfo.security,
      // hurry: securityInfo.hurry,
      approvalLine: documentInfo.approvalLine,
      recipients: documentInfo.recipients,
      executors: documentInfo.executors,
      referenceDocuments,
      attachments,
      summaryNote,
      useCorporateSeal,
    });

    // 수정 모드일 경우 docIdx 추가하여 '수정' 요청임을 명시
    if (isEditMode && docIdx) {
      documentData.docIdx = docIdx;
    }

    saveDocumentMutation.mutate(documentData);
  }

  // 상신 핸들러 - PC 버전과 동일한 검증 로직 적용
  async function handleSubmit() {
    // formId가 null, undefined, 또는 빈 문자열인 경우에만 에러 처리
    // formId가 0인 경우도 유효한 값으로 처리
    if (formInfo.formId == null || formInfo.formId === '') {
      showAlert('양식을 선택해주세요.');
      return;
    }

    if (!documentInfo.subject.trim()) {
      showAlert('제목을 입력해주세요.');
      return;
    }

    if (!editorContent.trim()) {
      showAlert('문서 내용을 입력해주세요.');
      return;
    }

    if (!documentInfo.executors || documentInfo.executors.length === 0) {
      showAlert('시행자를 지정해주세요.');
      return;
    }

    if (!companyCode || !enumber) {
      showAlert('로그인 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }

    // 서명 체크 - PC 버전과 동일한 로직
    try {
      const signResponse = await checkMySign();
      // API 응답 필드명: signImage (카멜케이스) 또는 signImg, sign_img (하위 호환)
      const hasSign =
        signResponse?.resultData?.signImage ||
        signResponse?.resultData?.signImg ||
        signResponse?.resultData?.sign_img;

      if (!hasSign) {
        showAlert('서명이 없습니다.\n서명을 등록해 주세요.');
        if (onSignRequired) {
          onSignRequired();
        }
        return;
      }
    } catch (error) {
      console.error('서명 확인 중 오류:', error);
      showAlert('서명 확인 중 오류가 발생했습니다.');
      return;
    }

    showConfirm('상신하시겠습니까?', async function () {
      setIsSubmitting(true);
      setSubmitType('submit'); // 상신 타입 설정

      // 문서 본문 이미지 캡쳐 — DOCUMENT_CAPTURE_DEFAULTS(공통 옵션) 사용.
      // 미리보기(captureHtmlAsDataUrl)도 같은 DOCUMENT_CAPTURE_DEFAULTS를 쓰므로 결과가 동일함.
      let contentsImg = null;
      if (editorContent) {
        try {
          contentsImg = await captureHtmlContentAsBase64(editorContent, {
            ...DOCUMENT_CAPTURE_DEFAULTS,
          });
        } catch (captureError) {
          console.error('문서 이미지 캡쳐 실패:', captureError);
          setIsSubmitting(false);
          setSubmitType(null);
          showAlert('문서 이미지 생성에 실패했습니다.\n다시 시도해주세요.');
          return;
        }
      }

      // 이미지 캡쳐 결과 검증 (본문이 있는데 캡쳐 결과가 없으면 실패)
      if (editorContent && !contentsImg) {
        setIsSubmitting(false);
        setSubmitType(null);
        showAlert('문서 이미지 생성에 실패했습니다.\n다시 시도해주세요.');
        return;
      }

      // 상신 모드 결정
      // 1. 재작성(isRewriteMode): 항상 mode 0 (새문서 상신) - 반려된 문서를 새 문서로 재작성
      // 2. 재상신(mode 2): 임시저장이 아니고 isEditMode와 docIdx가 있을 때
      // 3. 그 외: mode 0 (새문서 상신) - 임시저장 문서도 일반 상신으로 처리
      // docState가 null이면 임시저장 문서로 간주 (임시저장 문서는 항상 docState가 0이어야 함)
      const isTempDoc = docState === 0 || (isEditMode && docIdx && docState === null);
      let mode;
      if (isRewriteMode) {
        // 재작성: mode 0 (새문서 상신) - 반려된 문서 내용으로 새 문서 생성
        mode = 0;
      } else if (isEditMode && docIdx && !isTempDoc) {
        // 재상신: mode 2
        mode = 2;
      } else {
        // 새문서 상신: mode 0 (임시저장 문서도 일반 상신으로 처리)
        mode = 0;
      }

      // docState 설정: 항상 2로 설정 (백엔드에서 문서번호 생성에 필요)
      // 재상신(mode 2)일 때는 기존 docState 전달
      // 재작성 모드에서는 항상 새 문서이므로 2 (상신 상태)
      let finalDocState = 2; // 기본값: 상신 상태
      if (mode === 2 && isEditMode && docState && !isRewriteMode) {
        // 재상신일 때만 기존 docState 전달 (재작성은 제외)
        finalDocState = docState;
      }

      const documentData = buildDocumentData({
        mode,
        companyCode,
        enumber,
        docType: formInfo.formId,
        title: documentInfo.subject,
        contents: editorContent,
        contentsImg,
        docState: finalDocState,
        // [WEB 기획 제외] PC 버전에서는 보안등급/긴급여부 기능이 있으나, WEB 버전에서는 기획에서 제외됨
        // security: securityInfo.security,
        // hurry: securityInfo.hurry,
        approvalLine: documentInfo.approvalLine,
        recipients: documentInfo.recipients,
        executors: documentInfo.executors,
        referenceDocuments,
        attachments,
        summaryNote,
        useCorporateSeal,
      });

      // 재상신(mode 2)일 때: docIdx 전달
      if (mode === 2 && docIdx) {
        documentData.docIdx = docIdx;
      }

      saveDocumentMutation.mutate(documentData);
    });
  }

  /**
   * 임시저장 데이터 복구 핸들러
   * 저장된 임시저장 데이터가 있으면 복구하고 콜백 호출
   */
  function handleRestoreDraft() {
    const draft = loadDraft();
    if (draft && onDraftRestore) {
      onDraftRestore(draft);
      return true;
    }
    return false;
  }

  /**
   * 임시저장 데이터 삭제 (명시적 호출용)
   */
  function handleClearDraft() {
    clearDraft();
  }

  return {
    // 핸들러 함수
    handleTempSave,
    handleSubmit,
    handleRestoreDraft,
    handleClearDraft,
    // 상태
    isSubmitting,
    submitType, // 현재 진행 중인 작업 타입 ('temp' | 'submit' | null)
    isSaving: saveDocumentMutation.isPending,
    // 임시저장 관련
    hasDraft,
    getDraftSavedTime,
    loadDraft,
    saveDraft,
  };
}

export default useDocumentSave;
